import {networkOptimization} from '../../../../lib/api';
import {FileListResponse, FileDetailResponse} from '../types';

/**
 * Interface for processing files service operations.
 * Follows Dependency Inversion Principle - depend on abstractions, not concretions.
 */
export interface IProcessingFilesService {
    listFiles(page: number, pageSize: number, signal?: AbortSignal): Promise<FileListResponse>;
    getFileDetail(fileId: number, signal?: AbortSignal): Promise<FileDetailResponse>;
}

/**
 * API implementation of processing files service.
 * Uses the main API client to fetch data from backend endpoints.
 */
export class ApiProcessingFilesService implements IProcessingFilesService {
    async listFiles(page: number, pageSize: number, signal?: AbortSignal): Promise<FileListResponse> {
        return await networkOptimization.listFiles(page, pageSize, signal);
    }

    async getFileDetail(fileId: number, signal?: AbortSignal): Promise<FileDetailResponse> {
        return await networkOptimization.getFileDetail(fileId, signal);
    }
}

/**
 * Mock implementation of processing files service.
 * Used for testing and development without backend dependency.
 */
export class MockProcessingFilesService implements IProcessingFilesService {
    async listFiles(page: number, pageSize: number, _signal?: AbortSignal): Promise<FileListResponse> {
        // Return mock data structure
        return {
            files: [
                {
                    id: 1,
                    filename: 'mock-file-1.csv',
                    org_id: '100',
                    user_id: '1',
                    uploaded_by: 'Mock User',
                    status: 'processing',
                    current_stage: 'Stage 2: Ingestion',
                    run_name: 'Mock Run 1',
                    size_bytes: 1048576,
                    created_at: new Date().toISOString(),
                    processing_completed_at: null,
                    error_message: null,
                    error_report_file: null
                }
            ],
            current_page: page,
            total_pages: 1,
            total_rows: 1,
            page_size: pageSize
        };
    }

    async getFileDetail(fileId: number, _signal?: AbortSignal): Promise<FileDetailResponse> {
        // Return mock file detail
        return {
            id: fileId,
            filename: `mock-file-${fileId}.csv`,
            original_filename: `mock-file-${fileId}.csv`,
            org_id: '100',
            user_id: '1',
            uploaded_by: 'Mock User',
            status: 'processing',
            current_stage: 'Stage 2: Ingestion',
            run_name: 'Mock Run',
            databricks_run_id: 12345,
            size_bytes: 1048576,
            volume_path: '/mnt/volume/mock',
            error_report_path: null,
            error_message: null,
            validation_errors_count: 0,
            validation_warnings_count: 0,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            validation_completed_at: null,
            processing_started_at: new Date().toISOString(),
            processing_completed_at: null,
            events: []
        };
    }
}

/**
 * Default service instance.
 * Change to MockProcessingFilesService for testing without backend.
 */
export const processingFilesService: IProcessingFilesService = new ApiProcessingFilesService();
